import { useCallback, useMemo } from "react";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import { bulletsToActionQueue } from "@/utils/mistralInsightMappers";
import {
  AlertTriangle,
  ArrowLeftRight,
  Users,
  BarChart3,
  ShieldAlert,
} from "lucide-react";
import {
  buildTriGaugeStyle,
  buildDeploymentHeroHeadline,
  buildDeploymentHeroNarrative,
  buildDeploymentStatusChips,
  buildDeploymentPrimaryAction,
  buildDeploymentKpis,
  buildPressureSnapshotCards,
  buildDeputationDualRows,
  buildTriRiskRows,
  buildCapacityChart,
  buildRiskWatchlist,
  buildDeploymentActionQueue,
  buildDeploymentReviewNote,
  buildTeacherDeploymentMarkdown,
} from "@/utils/teacherDeploymentDerived";
import { toTitleCase } from "../utils/toTitleCase";

const SNAPSHOT_ICONS = {
  orange: ArrowLeftRight,
  purple: Users,
  red: AlertTriangle,
  green: BarChart3,
};

const ACTION_ICONS = {
  red: AlertTriangle,
  orange: ArrowLeftRight,
  purple: Users,
};

const scrollToId = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const TeacherDeploymentTab = ({
  overview = {},
  deploymentRisk = [],
  blockData = [],
}) => {
  const tri = overview.teacher_risk_index ?? 0;
  const hasData = Boolean(overview?.teachers_cy || blockData?.length);

  const buildLocalMarkdown = useCallback(
    () => buildTeacherDeploymentMarkdown(overview, blockData, deploymentRisk),
    [overview, blockData, deploymentRisk]
  );
  const scopeKey = useMemo(
    () => JSON.stringify({ t: overview?.teachers_cy, r: tri }),
    [overview?.teachers_cy, tri]
  );
  const {
    loading: aiLoading,
    pickHeadline,
    pickFromBucket,
    pickPrimaryAction,
    pickMapped,
    isMistral,
  } = useMistralNarrativeOverlay({
    kind: "teacher-deployment",
    title: "Teacher Deployment Risk",
    buildLocalMarkdown,
    context: { overview, blockData, deploymentRisk },
    enabled: hasData,
    scopeKey,
  });

  const gaugeStyle = useMemo(() => buildTriGaugeStyle(tri), [tri]);
  const localHeadline = useMemo(() => buildDeploymentHeroHeadline(), []);
  const localNarrative = useMemo(() => buildDeploymentHeroNarrative(blockData), [blockData]);
  const headline = useMemo(
    () => (aiLoading ? localHeadline : pickHeadline(localHeadline)),
    [aiLoading, pickHeadline, localHeadline]
  );
  const narrative = useMemo(
    () => (aiLoading ? localNarrative : pickFromBucket(localNarrative, "insights", 1)),
    [aiLoading, pickFromBucket, localNarrative]
  );
  const statusChips = useMemo(() => {
    const chips = buildDeploymentStatusChips(overview);
    if (isMistral && !aiLoading) {
      return [{ label: "Mistral AI Assisted", className: "purple" }, ...chips];
    }
    return chips;
  }, [overview, isMistral, aiLoading]);
  const localPrimaryAction = useMemo(
    () => buildDeploymentPrimaryAction(blockData),
    [blockData]
  );
  const primaryAction = useMemo(
    () => (aiLoading ? localPrimaryAction : pickPrimaryAction(localPrimaryAction)),
    [aiLoading, pickPrimaryAction, localPrimaryAction]
  );
  const kpis = useMemo(() => buildDeploymentKpis(overview), [overview]);
  const snapshotCards = useMemo(
    () => buildPressureSnapshotCards(overview, blockData),
    [overview, blockData]
  );
  const dualRows = useMemo(() => buildDeputationDualRows(deploymentRisk), [deploymentRisk]);
  const triRows = useMemo(() => buildTriRiskRows(blockData), [blockData]);
  const capacity = useMemo(() => buildCapacityChart(blockData), [blockData]);
  const watchlist = useMemo(() => buildRiskWatchlist(blockData), [blockData]);
  const localActionQueue = useMemo(
    () => buildDeploymentActionQueue(overview, blockData, deploymentRisk),
    [overview, blockData, deploymentRisk]
  );
  const actionQueue = useMemo(
    () => pickMapped(localActionQueue, "recs", bulletsToActionQueue),
    [pickMapped, localActionQueue]
  );
  const localReviewNote = useMemo(() => buildDeploymentReviewNote(overview), [overview]);
  const reviewNote = useMemo(
    () => (aiLoading ? localReviewNote : pickFromBucket(localReviewNote, "actions", 0)),
    [aiLoading, pickFromBucket, localReviewNote]
  );

  return (
    <div className="tdp-deploy-tab" data-testid="teacher-deployment">
      <section className="tdp-hero-card" id="tdp-hero">
        <div className="tdp-hero-layout">
          <div>
            <div className="tdp-risk-main">
              <div
                className="tdp-gauge"
                style={gaugeStyle}
                aria-label={`Teacher risk index ${tri}`}
              >
                <div>
                  <strong>{tri}</strong>
                  <span>TRI Score</span>
                </div>
              </div>
              <div className="tdp-risk-copy">
                <h2 className="dashboard-kpi-hero-title">{headline}</h2>
                <p>{narrative}</p>
                <div className="tdp-status-row">
                  {statusChips.map((chip) => (
                    <span
                      key={chip.label}
                      className={`tdp-status-chip ${chip.className}`.trim()}
                    >
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="tdp-summary-note">
              <div className="tdp-icon-box red">
                <AlertTriangle size={22} />
              </div>
              <div>
                <b>Primary executive action</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="tdp-metric-grid" aria-label="Deployment risk KPIs">
            {kpis.map((card) => (
              <article key={card.label} className={`tdp-metric-card ${card.cardClass}`.trim()}>
                <small>{card.label}</small>
                <strong>{card.value}</strong>
                <span>{card.sub}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="tdp-section-title" id="tdp-snapshot">
        <div>
          <h2>Deployment Pressure Snapshot</h2>
          <p>
            Priority indicators are grouped into four decision cards so officers can quickly
            separate staffing movement, shared teaching dependency and risk concentration.
          </p>
        </div>
        <button
          type="button"
          className="tdp-section-action"
          onClick={() => scrollToId("tdp-actions")}
        >
          View actions →
        </button>
      </div>

      <section className="tdp-domain-grid">
        {snapshotCards.map((card) => {
          const Icon = SNAPSHOT_ICONS[card.iconClass] ?? BarChart3;
          return (
            <article key={card.id} className="tdp-domain-card">
              <div className="tdp-domain-head">
                <div className={`tdp-icon-box ${card.iconClass}`.trim()}>
                  <Icon size={22} />
                </div>
                <span className={`tdp-status-dot ${card.dotClass}`.trim()} />
              </div>
              <h3>{toTitleCase(card.title)}</h3>
              <div className="tdp-domain-score">
                <strong>{card.score}</strong>
                <span>{card.scoreLabel}</span>
              </div>
              <div className="tdp-progress-line">
                <i style={{ width: `${card.width}%`, background: card.barColor }} />
              </div>
              <div className="tdp-domain-meta">
                <span>{card.metaLeft}</span>
                <span>{card.metaRight}</span>
              </div>
            </article>
          );
        })}
      </section>

      <div className="tdp-section-title" id="tdp-diagnostics">
        <div>
          <h2>Risk Diagnostics</h2>
          <p>
            The chart area is split into deployment movement and TRI watchlist to avoid
            overcrowding and to make the action path clearer.
          </p>
        </div>
        <button
          type="button"
          className="tdp-section-action"
          onClick={() => scrollToId("tdp-capacity")}
        >
          Capacity view →
        </button>
      </div>

      <section className="tdp-analytics-grid">
        <article className="tdp-panel">
          <div className="tdp-panel-head">
            <div>
              <h3>Deputation by Block</h3>
              <p>Compares teachers on deputation against teachers serving other schools.</p>
            </div>
            <span className="tdp-pill">Movement</span>
          </div>
          <div className="tdp-legend-row-inline">
            <span className="tdp-legend-item">
              <i className="tdp-swatch orange" />
              On Deputation
            </span>
            <span className="tdp-legend-item">
              <i className="tdp-swatch purple" />
              Other Schools
            </span>
          </div>
          <div className="tdp-dual-bars" aria-label="Dual bar chart for deputation by block">
            {dualRows.map((row) => (
              <div key={row.block} className="tdp-dual-row">
                <div className="tdp-dual-label">{row.block}</div>
                <div className="tdp-bar-stack">
                  <div className="tdp-track">
                    <i className="orange" style={{ width: `${row.deputationWidth}%` }} />
                  </div>
                  <div className="tdp-track">
                    <i className="purple" style={{ width: `${row.otherWidth}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </article>

        <article className="tdp-panel">
          <div className="tdp-panel-head">
            <div>
              <h3>Block Risk Index</h3>
              <p>TRI = BelowGrad% × 0.4 + Deputation% × 0.3 + CrossSchool% × 0.3.</p>
            </div>
            <span className="tdp-pill">TRI</span>
          </div>
          <div className="tdp-risk-list" aria-label="Block risk index list">
            {triRows.map((row) => (
              <div key={row.block} className="tdp-risk-row">
                <div className="tdp-risk-label">{row.block}</div>
                <div className="tdp-risk-track">
                  <i className={row.barClass} style={{ width: `${row.width}%` }} />
                </div>
                <div className="tdp-risk-value">{row.display}</div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="tdp-panel tdp-wide-panel" id="tdp-capacity">
        <div className="tdp-panel-head">
          <div>
            <h3>Block Staffing Capacity Analysis</h3>
            <p>
              Bars show total teachers. The line overlays average teachers per school to highlight
              capacity imbalance.
            </p>
          </div>
          <span className="tdp-pill">Teachers vs Avg/School</span>
        </div>
        <div className="tdp-capacity-chart" aria-label="Block staffing capacity chart">
          <div className="tdp-capacity-grid" />
          <svg
            className="tdp-line-svg"
            viewBox="0 0 980 205"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <polyline
              points={capacity.polyline}
              fill="none"
              stroke="#ef4444"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <g fill="#fff" stroke="#ef4444" strokeWidth="3">
              {capacity.circles.map((c, i) => (
                <circle key={i} cx={c.cx} cy={c.cy} r="5" />
              ))}
            </g>
          </svg>
          <div className="tdp-capacity-bars">
            {capacity.columns.map((col) => (
              <div key={col.block} className="tdp-cap-col">
                <div className="tdp-cap-bar" style={{ height: `${col.barHeight}px` }} />
                <div className="tdp-cap-label">{col.block}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="tdp-chart-legend">
          <span className="tdp-legend-item">
            <i className="tdp-swatch" />
            Total Teachers
          </span>
          <span className="tdp-legend-item">
            <i className="tdp-swatch red" />
            Avg / School
          </span>
        </div>
      </section>

      <div className="tdp-section-title" id="tdp-actions">
        <div>
          <h2>Block Review and Recommended Actions</h2>
          <p>
            The bottom section converts diagnostics into follow-up work: which blocks need review,
            why they are risky and what officers should do next.
          </p>
        </div>
        <button
          type="button"
          className="tdp-section-action"
          onClick={() => scrollToId("tdp-hero")}
        >
          Back to top ↑
        </button>
      </div>

      <section className="tdp-two-column">
        <article className="tdp-panel tdp-table-card">
          <div className="tdp-table-head">
            <div>
              <h3>Risk Watchlist</h3>
              <p>Blocks requiring staffing review during the next district meeting.</p>
            </div>
            <span className="tdp-pill">Top risk blocks</span>
          </div>
          <table className="tdp-watch-table" aria-label="Risk watchlist table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Block</th>
                <th>TRI</th>
                <th>Primary Driver</th>
                <th>Suggested Owner</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {watchlist.map((row) => (
                <tr key={row.block}>
                  <td>{row.rank}</td>
                  <td>{row.block}</td>
                  <td className="score">{row.tri}</td>
                  <td>{row.driver}</td>
                  <td>{row.owner}</td>
                  <td>
                    <span className={`tdp-tag ${row.statusClass}`.trim()}>{row.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>

        <article className="tdp-panel">
          <div className="tdp-panel-head">
            <div>
              <h3>Recommended Action Queue</h3>
              <p>Three officer-ready actions for deployment and risk governance.</p>
            </div>
            <span className="tdp-pill">Priority</span>
          </div>
          <div className="tdp-action-card">
            {actionQueue.map((item) => {
              const Icon = ACTION_ICONS[item.iconClass] ?? ShieldAlert;
              return (
                <div key={item.title} className="tdp-action-item">
                  <div className={`tdp-icon-box ${item.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="tdp-action-footer">
            <b>Next review recommendation</b>
            <span>{reviewNote}</span>
          </div>
        </article>
      </section>
    </div>
  );
};

export default TeacherDeploymentTab;
